/**
 * Who is waiting, and getting them out of here.
 *
 * The whole point of this panel is the export button. Deciding who to invite
 * happens in a mail client or a spreadsheet, not in an admin panel, so the job
 * here is to hand over a clean file and record what was done afterwards.
 *
 * Marking people invited is a separate, deliberate action rather than something
 * exporting does for you. Exporting is how you *look* at the list — doing it
 * twice is normal, and if looking marked people as invited, the second look
 * would come back empty and the record of who still needs one would be gone.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc, trpcClient } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { useToast } from "../../components/ui/Toast.js";
import "./WaitlistPanel.css";

export function WaitlistPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showInvited, setShowInvited] = useState(false);

  const list = useQuery(
    trpc.waitlist.list.queryOptions({
      includeInvited: showInvited,
      limit: 1000,
      offset: 0,
    }),
  );

  const markInvited = useMutation({
    mutationFn: (emails: string[]) =>
      trpcClient.waitlist.markInvited.mutate({ emails }),
    onSuccess: (result) => {
      toast(
        `${String(result.marked)} ${result.marked === 1 ? "person" : "people"} marked as invited.`,
      );
      void queryClient.invalidateQueries({ queryKey: ["waitlist"] });
      void list.refetch();
    },
    onError: () =>
      toast("That did not save. Try again in a moment.", { variant: "danger" }),
  });

  const entries = list.data?.entries ?? [];

  /*
   * Built here rather than on the server: the rows are already in the browser,
   * and a download endpoint would be a second place for the same list to leak
   * from. The file never travels.
   */
  const csv = useMemo(() => {
    const header = "email,joined,locale,source,invited";
    const rows = entries.map((entry) =>
      [
        escapeCsv(entry.email),
        entry.createdAt,
        escapeCsv(entry.locale ?? ""),
        escapeCsv(entry.source ?? ""),
        entry.invitedAt ?? "",
      ].join(","),
    );
    return [header, ...rows].join("\r\n");
  }, [entries]);

  const download = () => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    /* Dated, because the answer changes daily and two files called
       "waitlist.csv" in a downloads folder are indistinguishable. */
    anchor.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  const copyAddresses = async () => {
    const addresses = entries.map((entry) => entry.email).join(", ");
    try {
      await navigator.clipboard.writeText(addresses);
      toast(`${String(entries.length)} addresses copied.`);
    } catch {
      toast("Could not reach the clipboard. Use Export instead.", {
        variant: "danger",
      });
    }
  };

  return (
    <section className="fc-admin__section" aria-labelledby="fc-admin-waitlist">
      <h2 className="fc-admin__section-title" id="fc-admin-waitlist">
        Waitlist
      </h2>

      <div className="fc-waitlistp__counts">
        <Count label="Waiting" value={list.data?.waiting} />
        <Count label="Invited" value={list.data?.invited} />
        <Count label="Total" value={list.data?.total} />
      </div>

      <div className="fc-waitlistp__actions">
        <Button
          variant="primary"
          onClick={download}
          disabled={entries.length === 0}
        >
          Export CSV
        </Button>

        <Button
          variant="ghost"
          onClick={() => void copyAddresses()}
          disabled={entries.length === 0}
        >
          Copy addresses
        </Button>

        <Button
          variant="ghost"
          onClick={() => {
            const pending = entries
              .filter((entry) => entry.invitedAt === null)
              .map((entry) => entry.email);
            if (pending.length === 0) return;
            markInvited.mutate(pending);
          }}
          disabled={
            markInvited.isPending ||
            entries.every((entry) => entry.invitedAt !== null)
          }
        >
          {/* The label does not change while it works — see the note in the
              waitlist form about buttons that move under the cursor. */}
          Mark all as invited
        </Button>

        <label className="fc-waitlistp__toggle">
          <input
            type="checkbox"
            checked={showInvited}
            onChange={(event) => setShowInvited(event.target.checked)}
          />
          Show people already invited
        </label>
      </div>

      {list.isLoading && <p className="fc-waitlistp__empty">Loading…</p>}

      {list.isError && (
        <p className="fc-waitlistp__empty">
          The list could not be loaded. This needs the API deployed — the
          waitlist is new.
        </p>
      )}

      {list.isSuccess && entries.length === 0 && (
        <p className="fc-waitlistp__empty">
          {showInvited
            ? "Nobody has signed up yet."
            : "Nobody is waiting. Everyone who signed up has been invited."}
        </p>
      )}

      {entries.length > 0 && (
        <div className="fc-waitlistp__scroll">
          <table className="fc-waitlistp__table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Joined</th>
                <th scope="col">Language</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.email}</td>
                  <td className="fc-tabular">
                    {new Date(entry.createdAt).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </td>
                  <td>{entry.locale ?? "—"}</td>
                  <td>
                    {/* Text, not only colour — a status nobody can see is not
                        a status. */}
                    {entry.invitedAt ? "Invited" : "Waiting"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Count({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="fc-waitlistp__count">
      <span className="fc-waitlistp__count-value fc-tabular">
        {value ?? "—"}
      </span>
      <span className="fc-waitlistp__count-label">{label}</span>
    </div>
  );
}

/**
 * Quote a field for CSV.
 *
 * An address cannot contain a comma or a quote, but `source` is free text that
 * arrived from a URL, and a spreadsheet that splits one row into two because
 * nobody quoted a field is a quietly corrupted export.
 */
function escapeCsv(value: string): string {
  if (!/[",\r\n]/.test(value)) return value;
  return `"${value.replace(/"/g, '""')}"`;
}
